import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Loader2, Sparkles, FlaskConical, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";

const STRICTNESS = ["lenient", "balanced", "strict"];
const REC_STYLE = {
  Approve: { bg: "bg-emerald-50", ring: "border-emerald-200", text: "text-emerald-700", dot: "#059669" },
  Review: { bg: "bg-amber-50", ring: "border-amber-200", text: "text-amber-700", dot: "#D97706" },
  Reject: { bg: "bg-red-50", ring: "border-red-200", text: "text-red-700", dot: "#DC2626" },
};

export function AiConfig() {
  const [cfg, setCfg] = useState(null);
  const [busy, setBusy] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const nav = useNavigate();
  useEffect(() => { api.aiConfig().then(setCfg).catch(() => toast.error("Could not load AI settings")); }, []);

  const save = async () => {
    if (cfg.auto_approve_mode === "live" &&
        !window.confirm((cfg.auto_approve_on_submit
          ? "Enable LIVE auto-approve on submission? Eligible shifts will be approved for payroll & billing as soon as the worker submits, without anyone opening them."
          : "Enable LIVE auto-approve? When someone who can approve timesheets runs the AI review, eligible shifts will be approved for payroll & billing without them checking each one.")
          + " Prerequisites (clock-out, submitted notes, resolved expenses) are still enforced, and a shift outside the geofence, off its rostered hours, over its KM, with an incident or with a phone-time difference always goes to a person.")) return;
    setBusy(true);
    try { setCfg(await api.saveAiConfig(cfg)); toast.success("AI assistant settings saved"); }
    catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    finally { setBusy(false); }
  };
  const setCrit = (key, field, val) => setCfg((c) => ({ ...c, criteria: { ...c.criteria, [key]: { ...c.criteria[key], [field]: val } } }));

  const runTest = async () => {
    if (!cfg.model) return toast.error("Select a model to test");
    setTesting(true); setTestResult(null);
    try {
      const res = await api.testAiModel(cfg);
      setTestResult(res);
      if (res.parse_error) toast.warning("Model replied but not in the expected format — see raw output");
    } catch (e) { toast.error(e.response?.data?.detail || "Test failed"); }
    finally { setTesting(false); }
  };
  const activeLabel = (cfg?.models || []).find((m) => m.id === cfg?.model)?.label || cfg?.model;

  if (!cfg) return <div className="bg-white border border-slate-200 rounded-lg p-4 mt-3 flex justify-center"><Loader2 className="h-4 w-4 animate-spin text-slate-400" /></div>;

  return (
    <div className="bg-white border border-slate-200 rounded-lg p-4 mt-3" data-testid="ai-config">
      <div className="flex items-center justify-between">
        <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400 flex items-center gap-1.5"><Sparkles className="h-3 w-3" />AI Approval Assistant</div>
        <div className="flex gap-2">
          {[["on", true, "On"], ["off", false, "Off"]].map(([id, v, l]) => (
            <button key={id} onClick={() => setCfg((c) => ({ ...c, enabled: v }))} data-testid={`ai-enabled-${id}`}
              className={`text-xs font-semibold px-3 py-1.5 rounded-md border ${cfg.enabled === v ? "bg-[#bf3b4f] text-white border-[#bf3b4f]" : "bg-slate-50 text-slate-500 border-slate-200"}`}>{l}</button>
          ))}
        </div>
      </div>
      <p className="text-[11px] text-slate-500 mt-1 mb-3">Recommends Approve / Review / Reject for pending shifts, flags anything the office should know, and drafts an approval note. Runs on your <b>AWS Bedrock</b> account (Sydney, real-time). The office always makes the final call.</p>

      <div className="mb-3 rounded-md border border-slate-200 bg-slate-50/70 p-3" data-testid="ai-models">
        <div className="flex items-center justify-between mb-1">
          <label className="text-[11px] font-medium text-slate-600">Model</label>
          <button onClick={() => nav("/settings/ai")}
            className="text-[10px] text-slate-400 hover:text-slate-600 flex items-center gap-0.5 transition-colors">
            <ExternalLink className="h-3 w-3" /> Manage models in AI Settings
          </button>
        </div>
        <div className="flex gap-2">
          <select value={cfg.model || ""} onChange={(e) => { setCfg((c) => ({ ...c, model: e.target.value })); setTestResult(null); }}
            data-testid="ai-model-select" className="flex-1 text-xs border border-slate-200 rounded-md px-2 py-1.5 bg-white">
            {(cfg.models || []).map((m) => <option key={m.id} value={m.id}>{m.label}</option>)}
            {cfg.model && !(cfg.models || []).some((m) => m.id === cfg.model) && <option value={cfg.model}>{cfg.model}</option>}
          </select>
          <Button size="sm" variant="outline" className="h-8 text-xs shrink-0 border-rose-200 text-[#bf3b4f] hover:bg-rose-50"
            onClick={runTest} disabled={testing} data-testid="ai-model-test">
            {testing ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <FlaskConical className="h-3.5 w-3.5 mr-1" />}
            Test this model
          </Button>
        </div>
        <p className="text-[10px] text-slate-400 mt-1">To add or remove models, go to <b>Admin Settings &rarr; AI Settings</b>.</p>

        {testResult && (() => {
          const rs = REC_STYLE[testResult.recommendation] || REC_STYLE.Review;
          return (
            <div className={`mt-2 rounded-md border ${rs.ring} ${rs.bg} p-3`} data-testid="ai-test-result">
              {testResult.parse_error ? (
                <div>
                  <div className="text-[11px] font-semibold text-amber-700 mb-1">Unexpected output ({testResult.latency_ms} ms)</div>
                  <pre className="text-[10px] text-slate-600 whitespace-pre-wrap max-h-40 overflow-auto">{testResult.raw}</pre>
                </div>
              ) : (
                <>
                  <div className="flex items-center justify-between">
                    <span className={`inline-flex items-center gap-1.5 text-xs font-bold ${rs.text}`} data-testid="ai-test-recommendation">
                      <span className="h-2 w-2 rounded-full" style={{ backgroundColor: rs.dot }} />{testResult.recommendation}
                      <span className="text-slate-400 font-normal">&middot; {testResult.confidence}% confidence</span>
                    </span>
                    <span className="text-[10px] text-slate-400 font-mono">{testResult.latency_ms} ms &middot; {activeLabel}</span>
                  </div>
                  {testResult.summary && <p className="text-[11px] text-slate-700 mt-1.5">{testResult.summary}</p>}
                  {testResult.flags?.length > 0 && (
                    <ul className="mt-1.5 space-y-0.5">
                      {testResult.flags.map((f, i) => <li key={i} className="text-[10px] text-slate-500 flex gap-1"><span className="text-amber-500">&bull;</span>{typeof f === "string" ? f : (f.issue || JSON.stringify(f))}</li>)}
                    </ul>
                  )}
                  {testResult.note_review && <div className="mt-1.5 text-[10px] text-slate-500"><b className="text-slate-600">Note review:</b> {testResult.note_review}</div>}
                  {testResult.drafted_note && <div className="mt-1.5 text-[10px] text-slate-500 italic border-l-2 border-slate-200 pl-2">&ldquo;{testResult.drafted_note}&rdquo;</div>}
                </>
              )}
            </div>
          );
        })()}
      </div>

      <div className="mb-3">
        <label className="text-[11px] font-medium text-slate-600">Strictness</label>
        <div className="flex gap-2 mt-1">
          {STRICTNESS.map((s) => (
            <button key={s} onClick={() => setCfg((c) => ({ ...c, strictness: s }))} data-testid={`ai-strict-${s}`}
              className={`text-xs font-semibold px-3 py-1.5 rounded-md border capitalize ${cfg.strictness === s ? "bg-slate-800 text-white border-slate-800" : "bg-slate-50 text-slate-500 border-slate-200"}`}>{s}</button>
          ))}
        </div>
      </div>

      <div className="mb-3">
        <label className="text-[11px] font-medium text-slate-600">Signals to weigh <span className="text-slate-400">(mark hard blockers &mdash; a failed blocker prevents an Approve recommendation)</span></label>
        <div className="mt-1.5 space-y-1">
          {Object.entries(cfg.criteria).map(([key, c]) => (
            <div key={key} className="flex items-center justify-between text-xs py-1 border-b border-slate-50" data-testid={`ai-crit-${key}`}>
              <label className="flex items-center gap-1.5 cursor-pointer flex-1">
                <input type="checkbox" checked={c.enabled} onChange={(e) => setCrit(key, "enabled", e.target.checked)} data-testid={`ai-crit-${key}-enabled`} />
                <span className="text-slate-700">{c.label}</span>
              </label>
              <label className="flex items-center gap-1.5 cursor-pointer">
                <input type="checkbox" checked={c.blocker} onChange={(e) => setCrit(key, "blocker", e.target.checked)} data-testid={`ai-crit-${key}-blocker`} />
                <span className="text-[10px] font-semibold uppercase tracking-wider text-red-500">Blocker</span>
              </label>
            </div>
          ))}
        </div>
      </div>

      <div className="mb-3">
        <label className="text-[11px] font-medium text-slate-600">Shift-note expectations</label>
        <Textarea rows={3} value={cfg.note_expectations} onChange={(e) => setCfg((c) => ({ ...c, note_expectations: e.target.value }))}
          className="text-xs mt-1" data-testid="ai-note-expectations" placeholder="Describe what good shift notes must contain&hellip;" />
      </div>
      <div className="mb-3">
        <label className="text-[11px] font-medium text-slate-600">Extra instructions <span className="text-slate-400">(optional)</span></label>
        <Textarea rows={2} value={cfg.custom_instructions} onChange={(e) => setCfg((c) => ({ ...c, custom_instructions: e.target.value }))}
          className="text-xs mt-1" data-testid="ai-custom-instructions" placeholder="Anything else the assistant should consider&hellip;" />
      </div>

      <div className="mb-3 rounded-md border border-slate-200 bg-slate-50/70 p-3" data-testid="ai-autoapprove">
        <label className="text-[11px] font-medium text-slate-600">Auto-approve mode</label>
        <p className="text-[10px] text-slate-400 mt-0.5 mb-1.5">Only ever eligible when the AI recommends <b>Approve</b>, confidence is at or above the threshold, and no hard-blocker criterion fails.</p>
        <div className="flex gap-2">
          {[["off", "Off", "AI recommends only"],
            ["shadow", "Shadow", "Marks would-approve; human still confirms"],
            ["live", "Live", "AI approves eligible shifts when an approver runs the review"]].map(([id, l, hint]) => (
            <button key={id} onClick={() => setCfg((c) => ({ ...c, auto_approve_mode: id }))} data-testid={`ai-auto-${id}`}
              title={hint}
              className={`flex-1 text-xs font-semibold px-3 py-1.5 rounded-md border capitalize ${cfg.auto_approve_mode === id ? (id === "live" ? "bg-red-600 text-white border-red-600" : "bg-slate-800 text-white border-slate-800") : "bg-white text-slate-500 border-slate-200"}`}>{l}</button>
          ))}
        </div>
        <div className="mt-2 flex items-center gap-2">
          <label className="text-[11px] font-medium text-slate-600">Min confidence</label>
          <input type="number" min={80} max={100} value={cfg.min_confidence ?? 90}
            onChange={(e) => setCfg((c) => ({ ...c, min_confidence: parseInt(e.target.value || "0", 10) }))}
            onBlur={() => setCfg((c) => ({ ...c, min_confidence: Math.max(80, Math.min(100, c.min_confidence || 90)) }))}
            data-testid="ai-min-confidence" className="w-16 text-xs border border-slate-200 rounded-md px-2 py-1" />
          <span className="text-[11px] text-slate-400">% (80–100)</span>
        </div>
        {cfg.auto_approve_mode === "shadow" && <p className="text-[10px] text-slate-500 mt-2" data-testid="ai-auto-shadow-hint">Shadow mode is safe: the office still approves every shift manually. Eligible shifts are simply marked so you can see how live mode would behave.</p>}
        {cfg.auto_approve_mode === "live" && (
          <label className="mt-2 flex items-start gap-2 text-[11px] text-slate-600" data-testid="ai-auto-on-submit">
            <input type="checkbox" className="mt-0.5" checked={!!cfg.auto_approve_on_submit}
              onChange={(e) => setCfg((c) => ({ ...c, auto_approve_on_submit: e.target.checked }))} />
            <span><b>Approve on submission</b> (not recommended). Off: the AI approves only when someone who can approve timesheets runs the AI review, and the history names them. On: it also approves as soon as the worker submits, with nobody opening the timesheet.</span>
          </label>
        )}
        {cfg.auto_approve_mode === "live" && <p className="text-[10px] text-red-600 font-semibold mt-2" data-testid="ai-auto-live-hint">Live mode approves eligible shifts for payroll &amp; billing {cfg.auto_approve_on_submit ? "as soon as the worker submits, without a human" : "when an approver runs the AI review, without them checking each one"}. Prerequisites (clock-out, submitted notes, resolved expenses) are still enforced, and a shift outside the geofence, off its rostered hours, over its KM, with an incident or with a phone-time difference always goes to a person.</p>}
      </div>

      <Button size="sm" onClick={save} disabled={busy} data-testid="ai-config-save" style={{ backgroundColor: "#bf3b4f" }}>
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save AI settings"}
      </Button>
    </div>
  );
}
